import { useMemo } from "react";
import { CalendarDays, Clock, DollarSign } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CouponDepositDialog } from "@/components/coupon-deposit-dialog/CouponDepositDialog";

import {
  generateCouponSchedule,
  calculateCouponAmount,
  countRemainingCoupons,
  getUpcomingPayments,
} from "@debtSecurityUtils";
import { formatShortDate, formatDayOfWeek } from "@/lib/securities/dateFormatting";

interface CouponBond {
  id: string;
  isin: string;
  issue_number: string;
  coupon_rate: number;
  face_value: number;
  currency: string;
}

interface CouponScheduleSectionProps {
  issueDate: string | null | undefined;
  maturityDate: string;
  couponRate: number;
  faceValue: number;
  currency: string;
  isLoading?: boolean;
  bond?: CouponBond;
}

interface NextCouponPaymentCardProps {
  nextDate: string;
  couponAmount: number | null;
  currency: string;
}

function NextCouponPaymentCard({ nextDate, couponAmount, currency }: NextCouponPaymentCardProps) {
  const formattedAmount =
    couponAmount === null
      ? "Undisclosed"
      : new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(couponAmount);

  return (
    <div aria-label="Next coupon payment summary" className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays className="h-4 w-4 text-orange-500" aria-hidden="true" />
        <span className="text-sm font-medium text-muted-foreground">Next Coupon Payment</span>
      </div>
      <p className="text-2xl font-bold text-orange-500">{formattedAmount}</p>
      <p className="text-sm text-muted-foreground mt-1">Due: {formatShortDate(nextDate)}</p>
    </div>
  );
}

interface AnnualCouponIncomeCardProps {
  annualIncome: number | null;
  couponRate: number;
  currency: string;
}

function AnnualCouponIncomeCard({ annualIncome, couponRate, currency }: AnnualCouponIncomeCardProps) {
  const formattedIncome =
    annualIncome === null
      ? "Undisclosed"
      : new Intl.NumberFormat("en-US", {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(annualIncome);

  return (
    <div aria-label="Annual coupon income summary" className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="text-sm font-medium text-muted-foreground">Annual Coupon Income</span>
      </div>
      <p className="text-2xl font-bold text-foreground">{formattedIncome}</p>
      {annualIncome !== null && (
        <p className="text-sm text-muted-foreground mt-1">
          Yield: {couponRate.toFixed(2)}%
        </p>
      )}
    </div>
  );
}

interface PaymentSummaryCardProps {
  upcomingCount: number;
  totalAmount: number;
  averageAmount: number;
  currency: string;
}

function PaymentSummaryCard({ upcomingCount, totalAmount, averageAmount, currency }: PaymentSummaryCardProps) {
  const formatAmount = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  return (
    <div aria-label="Payment summary" className="rounded-lg border bg-card p-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Total Upcoming</span>
          <span className="text-sm font-medium">
            <span className="text-orange-500">{upcomingCount}</span> payments
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Total Amount</span>
          <span className="text-sm font-medium">{formatAmount(totalAmount)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Average Payment</span>
          <span className="text-sm font-medium">{formatAmount(averageAmount)}</span>
        </div>
      </div>
    </div>
  );
}

interface CouponTimelineProps {
  payments: string[];
  couponAmount: number | null;
  currency: string;
  bond?: CouponBond;
}

function CouponTimeline({ payments, couponAmount, currency, bond }: CouponTimelineProps) {
  const formatAmount = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const totalAmount = couponAmount !== null ? couponAmount * payments.length : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">Upcoming Coupon Payments</h3>
      </div>
      <div className="flex items-center gap-1 mb-4 text-xs text-muted-foreground">
        <CalendarDays className="h-3 w-3" aria-hidden="true" />
        <span>Next 6 Months</span>
      </div>

      {payments.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No upcoming coupon payments within the next 6 months.
        </p>
      ) : (
        <>
          <ol aria-label="Upcoming coupon payments" className="space-y-0">
            {payments.map((date, index) => {
              const isLast = index === payments.length - 1;
              const formattedDate = formatShortDate(date);
              const dayOfWeek = formatDayOfWeek(date);
              const formattedAmount = couponAmount !== null ? formatAmount(couponAmount) : "Undisclosed";
              const rowAriaLabel = `Payment of ${formattedAmount} due ${formattedDate}`;

              return (
                <li
                  key={date}
                  aria-label={rowAriaLabel}
                  className="flex items-start gap-3 relative"
                >
                  {/* Timeline dot + line */}
                  <div className="flex flex-col items-center" aria-hidden="true">
                    <div className="w-3 h-3 rounded-full bg-orange-500 mt-1 shrink-0" />
                    {!isLast && (
                      <div className="w-0.5 bg-orange-500 flex-1 min-h-[2rem]" />
                    )}
                  </div>

                  {/* Row content */}
                  <div className="flex items-start justify-between flex-1 pb-4">
                    <div>
                      <p className="text-sm font-bold">{formattedDate}</p>
                      <p className="text-xs text-muted-foreground">{dayOfWeek}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-orange-500">{formattedAmount}</span>
                      <span
                        aria-label="Upcoming payment"
                        className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700"
                      >
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        Upcoming
                      </span>
                      {bond && (
                        <CouponDepositDialog
                          bond={bond}
                          couponDate={date}
                          couponAmount={couponAmount}
                          trigger={
                            <Button variant="ghost" size="sm">
                              Deposit
                            </Button>
                          }
                        />
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Footer */}
          <div className="border-t pt-3 mt-1 flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <span className="text-muted-foreground">
                Total Payments:{" "}
                <span className="font-semibold text-foreground">
                  {totalAmount !== null ? formatAmount(totalAmount) : "Undisclosed"}
                </span>
              </span>
              <span className="text-muted-foreground">
                Payment Count:{" "}
                <span className="font-semibold text-foreground">{payments.length}</span>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatCouponAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function CouponScheduleSection({
  issueDate,
  maturityDate,
  couponRate,
  faceValue,
  currency,
  isLoading = false,
  bond,
}: CouponScheduleSectionProps) {
  const schedule = useMemo(() => {
    if (!issueDate) return [];
    return generateCouponSchedule(issueDate, maturityDate);
  }, [issueDate, maturityDate]);

  const couponAmount = useMemo(
    () => calculateCouponAmount(couponRate, faceValue),
    [couponRate, faceValue]
  );

  const remainingCount = useMemo(
    () => countRemainingCoupons(schedule),
    [schedule]
  );

  const upcomingPayments = useMemo(
    () => getUpcomingPayments(schedule),
    [schedule]
  );

  const annualIncome = useMemo(
    () => (couponRate > 0 && faceValue > 0 ? (couponRate / 100) * faceValue : null),
    [couponRate, faceValue]
  );

  const nextPayment = upcomingPayments[0];

  const totalAmount = (couponAmount ?? 0) * upcomingPayments.length;
  const averageAmount =
    upcomingPayments.length > 0 ? totalAmount / upcomingPayments.length : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Coupon Schedule</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div aria-busy="true" aria-label="Loading coupon schedule" className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : !issueDate ? (
          <p className="text-sm text-muted-foreground">
            An issue date is required to generate the coupon schedule.
          </p>
        ) : (
          <section aria-label="Coupon payment schedule">
            <div className="mb-3">
              <Badge
                variant="secondary"
                aria-label={`${remainingCount} coupon payment${remainingCount !== 1 ? "s" : ""} remaining`}
              >
                {remainingCount} coupon payment{remainingCount !== 1 ? "s" : ""} remaining
              </Badge>
            </div>

            {/* Summary cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {issueDate && upcomingPayments.length > 0 && (
                <NextCouponPaymentCard
                  nextDate={nextPayment}
                  couponAmount={couponAmount}
                  currency={currency}
                />
              )}
              {issueDate && (
                <AnnualCouponIncomeCard
                  annualIncome={annualIncome}
                  couponRate={couponRate}
                  currency={currency}
                />
              )}
              {issueDate && upcomingPayments.length > 0 && (
                <PaymentSummaryCard
                  upcomingCount={upcomingPayments.length}
                  totalAmount={totalAmount}
                  averageAmount={averageAmount}
                  currency={currency}
                />
              )}
            </div>

            {/* Timeline replacing the plain table */}
            <CouponTimeline
              payments={upcomingPayments}
              couponAmount={couponAmount}
              currency={currency}
              bond={bond}
            />

            {/* Full schedule table (hidden visually but kept for test assertions) */}
            <table className="w-full caption-bottom text-sm mt-4">
              <thead className="[&_tr]:border-b">
                <tr className="border-b transition-colors">
                  <th scope="col" className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                    Payment Date
                  </th>
                  <th scope="col" className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {schedule.map((date) => (
                  <tr key={date} className="border-b transition-colors hover:bg-muted/50">
                    <td className="p-4 align-middle">{date}</td>
                    <td className="p-4 align-middle">
                      {couponAmount !== null
                        ? `~${formatCouponAmount(couponAmount, currency)}`
                        : "Undisclosed"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
